import React, { useState } from 'react';
import { Box, Button, MenuItem, TextField, Typography } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useSnapshot } from 'valtio';
import getStateManager from '../../StateManager';
import { canOpenFirmwareRecovery } from '../../Utils/system_settings_utils';

export default function ManualFirmwareUpdate({ controller, onClose }) {
  if (!controller) return null;
  return <ManualFirmwareUpdateForm key={controller.id} controller={controller} onClose={onClose} />;
}

function ManualFirmwareUpdateForm({ controller, onClose }) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const liveController = useSnapshot(controller);
  const [filename, setFilename] = useState('');
  const [target, setTarget] = useState('gc');
  const [confirming, setConfirming] = useState(false);

  if (!canOpenFirmwareRecovery(liveController)) return null;

  const writeAccess = liveController.access_rights.timing?.write === true;
  // Like recovery, this remains usable before complete settings arrive.
  const connected = liveController.connection.status !== 'bad';
  const canSubmit = writeAccess && connected && filename.trim() !== '';
  const targetLabel = t(target === 'gc' ? 'update_gc_firmware' : 'update_wifi_firmware');
  const system = liveController.settings.system;

  const submit = () => {
    if (!confirming || !canSubmit) return;
    const name = filename.trim();
    const command = target === 'gc' ? 'do_gc_firmware_update' : 'do_esp32_firmware_update';
    // FirmwareUpdate always carries both fields; the command selects the target.
    const data = {
      name,
      gc_firmware: target === 'gc' ? name : '---',
      esp_firmware: target === 'esp' ? name : '---'
    };
    try {
      if (!getStateManager().wsManager.sendWSMsg(controller, command, data)) {
        enqueueSnackbar(t('manual_firmware_send_failed'), { variant: 'error' });
        return;
      }
    } catch (error) {
      enqueueSnackbar(t('manual_firmware_send_failed'), { variant: 'error' });
      return;
    }
    enqueueSnackbar(
      t(target === 'gc' ? 'start_gc_firmware_update' : 'start_wifi_firmware_update'),
      {
        variant: 'info'
      }
    );
    setConfirming(false);
    setFilename('');
    onClose();
  };

  return (
    <Box mt={3}>
      <Typography variant="h6" gutterBottom>
        {t('manual_firmware_update')}
      </Typography>
      <Alert severity="warning">{t('manual_firmware_support_only')}</Alert>
      <Box my={2}>
        <Typography>
          <strong>{liveController.name || liveController.id}</strong> ({liveController.id})
        </Typography>
        <Typography>
          {t('info_controller_type')}: {liveController.type}
        </Typography>
        <Typography>
          {t('current_firware_version_main_controller')}:{' '}
          {system?.fw_version_number || t('unknown')}
        </Typography>
        <Typography>
          {t('current_firware_version_wifi')}: {system?.esp32_firmware_name || t('unknown')}
        </Typography>
      </Box>
      {confirming ? (
        <>
          <Typography variant="h6" gutterBottom>
            {t('confirm_firmware_update_title')}
          </Typography>
          <Typography>
            {t('manual_firmware_target')}: {targetLabel}
          </Typography>
          <Typography style={{ overflowWrap: 'anywhere' }}>
            {t('manual_firmware_filename')}: <strong>{filename.trim()}</strong>
          </Typography>
          <Typography paragraph>{t('manual_firmware_confirm_info')}</Typography>
          <Button color="primary" onClick={() => setConfirming(false)}>
            {t('cancel')}
          </Button>
          <Button color="primary" variant="contained" disabled={!canSubmit} onClick={submit}>
            {t('apply_firmware_update')}
          </Button>
        </>
      ) : (
        <>
          <TextField
            select
            fullWidth
            margin="normal"
            id="manual-firmware-target"
            label={t('manual_firmware_target')}
            value={target}
            onChange={(event) => setTarget(event.target.value)}
          >
            <MenuItem value="gc">{t('update_gc_firmware')}</MenuItem>
            <MenuItem value="esp">{t('update_wifi_firmware')}</MenuItem>
          </TextField>
          <TextField
            fullWidth
            margin="normal"
            id="manual-firmware-filename"
            label={t('manual_firmware_filename')}
            helperText={t('manual_firmware_filename_info')}
            value={filename}
            onChange={(event) => setFilename(event.target.value)}
            inputProps={{ spellCheck: false, autoCapitalize: 'none' }}
          />
          <Button
            color="primary"
            variant="outlined"
            disabled={!canSubmit}
            onClick={() => setConfirming(true)}
          >
            {t('manual_firmware_review')}
          </Button>
        </>
      )}
      {(!writeAccess || !connected) && (
        <Typography color="error">
          {t(
            !writeAccess
              ? 'disabled_fw_btn_no_write_access'
              : 'disabled_fw_btn_waiting_for_connection'
          )}
        </Typography>
      )}
    </Box>
  );
}
